import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Portal } from 'react-portal';
import _ from 'lodash';

import { buttonsV2 as Buttons, Icon } from '@breezeway/brzw-components';
import { FLYOUT_CONTENT_ID, removeFlyout } from '../../utils/flyout';

import './flyout_header.scss';

const CLASS_NAME = 'brzw-flyout-header';
const FLYOUT_HEADER_PORTAL_ID = 'flyout-header-portal';

const {
  BUTTON_THEMES: { ICON_SUBTLE },
  BUTTON_SIZES: { ICON_MEDIUM },
} = Buttons.constants;

const VARIANT = {
  INLINE_TITLE: 'inline-title',
  DEFAULT: 'default',
};

const renderActions = (actions, customAction, withCloseIcon) => (
  <div className={`${CLASS_NAME}__actions`}>
    {_.map(actions, action => (
      <Buttons.IconButton
        className={`${CLASS_NAME}__action-button`}
        iconName={_.get(action, 'icon')}
        key={`action-${_.get(action, 'icon')}`}
        onClick={_.get(action, 'onClick')}
        size={ICON_MEDIUM}
        theme={_.get(action, 'theme') || ICON_SUBTLE}
      />
    ))}
    {customAction && <div className={`${CLASS_NAME}__action-button`}>{customAction}</div>}
    {withCloseIcon && (
      <Buttons.IconButton
        iconName="close"
        onClick={removeFlyout}
        size={ICON_MEDIUM}
        theme={ICON_SUBTLE}
      />
    )}
  </div>
);

const renderTitle = (title, icon, subtitle) => (
  <div className={`${CLASS_NAME}__title-row`}>
    {icon && <Icon className={`${CLASS_NAME}__title-icon`} iconName={icon} />}
    <div className={`${CLASS_NAME}__title`}>{title}</div>
    {subtitle && (
      <div className={`${CLASS_NAME}__subtitle-inline`}>{subtitle}</div>
    )}
  </div>
);

const FlyoutHeader = props => {
  const [isFlyoutOpened, setIsFlyoutOpened] = useState(false);
  const [isFlyoutScrolling, setIsFlyoutScrolling] = useState(false);
  const handleContentScroll = ({ target }) => {
    const scrollTop = _.get(target, 'scrollTop', 0);
    setIsFlyoutScrolling(!_.isEqual(scrollTop, 0));
  };
  useEffect(() => {
    setIsFlyoutOpened(true);

    const flyoutContent = _.invoke(document, 'getElementById', FLYOUT_CONTENT_ID);
    if (flyoutContent) flyoutContent.addEventListener('scroll', handleContentScroll);

    return () => {
      if (flyoutContent) flyoutContent.removeEventListener('scroll', handleContentScroll);
    };
  }, []);

  const {
    aboveTitleElement,
    actions,
    children,
    className,
    customAction,
    shouldUsePortal,
    subTitle,
    title,
    titleIcon,
    withCloseIcon,
    withoutBorder,
    variant,
  } = props;

  const inlineSubtitle = variant === VARIANT.INLINE_TITLE && subTitle;

  const renderContent = () => (
    <div
      className={classnames(CLASS_NAME, className, {
        [`${CLASS_NAME}--without-border`]: withoutBorder,
        [`${CLASS_NAME}--is-flyout-scrolling`]: isFlyoutScrolling && shouldUsePortal,
        [`${CLASS_NAME}--${variant}`]: variant,
      })}
    >
      <div className={`${CLASS_NAME}__main-section`}>
        {title && !aboveTitleElement && renderTitle(title, titleIcon, inlineSubtitle)}
        {aboveTitleElement && aboveTitleElement}
        {renderActions(actions, customAction, withCloseIcon)}
      </div>
      {title && aboveTitleElement && renderTitle(title, titleIcon)}
      {subTitle && variant === VARIANT.DEFAULT && (
        <div className={`${CLASS_NAME}__subtitle`}>{subTitle}</div>
      )}
      {children}
    </div>
  );

  if (!shouldUsePortal) {
    return renderContent();
  }

  return (
    isFlyoutOpened && (
      <Portal node={document && document.getElementById(FLYOUT_HEADER_PORTAL_ID)}>
        {renderContent()}
      </Portal>
    )
  );
};

FlyoutHeader.propTypes = {
  aboveTitleElement: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      theme: PropTypes.string,
    }),
  ),
  children: PropTypes.shape({}),
  className: PropTypes.string,
  customAction: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string, PropTypes.node]),
  shouldUsePortal: PropTypes.bool,
  subTitle: PropTypes.string,
  title: PropTypes.string,
  titleIcon: PropTypes.string,
  withCloseIcon: PropTypes.bool,
  withoutBorder: PropTypes.bool,
  variant: PropTypes.oneOfType(_.values(VARIANT)),
};

FlyoutHeader.defaultProps = {
  className: '',
  shouldUsePortal: true,
  withCloseIcon: true,
  variant: VARIANT.DEFAULT,
};

export default FlyoutHeader;
